import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Check, X, Sparkles, Zap, Building2, Crown,
  Users, Cloud, Palette, Download, Lock, Headphones,
  ChevronDown, ChevronUp, ArrowRight, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import usePageMeta from '../lib/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

// Pricing in INR. `yearly` is the TOTAL annual price (not per-month).
// Enterprise is billed per seat/month with a 3-seat minimum.
const pricingPlans = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for getting started',
    price: { monthly: 0, yearly: 0 },
    icon: Sparkles,
    color: 'from-gray-500 to-gray-600',
    bgColor: 'bg-gray-50',
    popular: false,
    features: [
      { name: 'Unlimited designs', included: true },
      { name: 'Basic templates', included: true },
      { name: 'PNG export (max 1080px)', included: true },
      { name: '2GB storage', included: true },
      { name: '20 AI credits / month', included: true },
      { name: 'Premium templates', included: false },
      { name: 'PDF & SVG export', included: false },
      { name: 'Background remover', included: false },
      { name: 'Brand kit', included: false },
      { name: 'Priority support', included: false },
    ],
    cta: 'Get Started Free',
    ctaVariant: 'outline'
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Founding member pricing',
    price: { monthly: 2.99, yearly: 24 },
    founding: true,
    icon: Zap,
    color: 'from-violet-600 to-fuchsia-600',
    bgColor: 'bg-gradient-to-br from-violet-50 to-fuchsia-50',
    popular: true,
    features: [
      { name: 'Unlimited designs', included: true },
      { name: 'Premium templates', included: true },
      { name: 'PDF export', included: true },
      { name: 'SVG export', included: true },
      { name: 'Background remover', included: true },
      { name: 'Brand kit', included: true },
      { name: '50GB storage', included: true },
      { name: '300 AI credits / month', included: true },
      { name: 'AI credit top-ups available', included: true },
      { name: 'Team collaboration', included: false },
    ],
    cta: 'Become a Founding Member',
    ctaVariant: 'default'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For teams & organizations',
    price: { monthly: 7.99, yearly: 7.99 },
    perSeat: true,
    minSeats: 3,
    icon: Building2,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50',
    popular: false,
    features: [
      { name: 'Everything in Pro', included: true },
      { name: 'Team collaboration', included: true },
      { name: 'Shared workspaces', included: true },
      { name: 'Multiple brand kits', included: true },
      { name: 'API access', included: true },
      { name: 'Priority support', included: true },
      { name: '500GB+ pooled storage', included: true },
      { name: '1,000 AI credits per seat (pooled)', included: true },
    ],
    cta: 'Contact Sales',
    ctaVariant: 'outline'
  }
];

const faqs = [
  {
    question: 'Will my price increase later?',
    answer: 'No. Founding members keep their pricing forever. As long as your subscription stays active, your founding rate never increases — even when we raise prices for new users.'
  },
  {
    question: 'What is an AI Credit?',
    answer: '1 credit = 1 AI image generation OR 1 background removal. Free includes 20 credits/month, Pro includes 300, and Enterprise includes 1,000 per seat (pooled). You can buy more anytime with top-ups.'
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: 'Yes, you can cancel your subscription at any time. If you cancel, you\'ll still have access to Pro features until the end of your billing period.'
  },
  {
    question: 'What happens to my designs if I downgrade?',
    answer: 'Your designs are safe! You\'ll still be able to access and view all your designs. However, you won\'t be able to edit designs that use Pro features until you upgrade again.'
  },
  {
    question: 'Is there a free trial for Pro?',
    answer: 'Yes! We offer a 14-day free trial for Pro. No credit card required. You can explore all Pro features and decide if it\'s right for you.'
  },
  {
    question: 'Can I switch between monthly and yearly billing?',
    answer: 'Absolutely! You can switch between monthly and yearly billing at any time. If you switch to yearly, you\'ll save 33% on your subscription ($2.99 × 12 = $35.88 vs $24/year).'
  },
  {
    question: 'Do you offer discounts for nonprofits or education?',
    answer: 'Yes, we offer special pricing for nonprofits, educational institutions, and students. Contact our sales team for more information.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and in some regions, local payment methods.'
  }
];

const PricingPage = () => {
  const navigate = useNavigate();
  usePageMeta('Pricing', 'Compare iVisora plans: start free, upgrade when you need more.', { path: '/pricing' });
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [openFaq, setOpenFaq] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null); // plan id being loaded
  const [checkoutError, setCheckoutError] = useState('');

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handlePlanClick = async (planId) => {
    if (planId === 'free') {
      navigate('/editor');
      return;
    }
    if (planId === 'enterprise') {
      navigate('/contact?topic=sales');
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      // Not logged in — send to editor where auth modal will appear
      navigate('/editor');
      return;
    }
    if (!API_URL) {
      navigate('/editor');
      return;
    }

    setLoadingPlan(planId);
    setCheckoutError('');
    try {
      const res = await fetch(`${API_URL}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan: planId, billing: billingCycle }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start checkout.');
      window.location.href = data.checkout_url;
    } catch (err) {
      setCheckoutError(err.message || 'Something went wrong. Please try again.');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4">
            🚀 Founding Member Pricing — Limited Time
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
            Premium AI Design Platform at Founding Member Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Start free and create without limits. Join the first 5,000 creators and lock in lifetime pricing.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white rounded-full p-1.5 shadow-lg border">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 rounded-full font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yearly
              <Badge className="bg-green-500 text-white border-0 text-xs">
                Save 33%
              </Badge>
            </button>
          </div>
        </div>
      </section>

      {/* Founding Member Offer banner */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10 mb-6">
        <div className="max-w-6xl mx-auto rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-xl px-6 py-6 md:px-10 md:py-7">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-center md:text-left">
            <div>
              <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                <span className="text-2xl">🚀</span>
                <h3 className="text-xl md:text-2xl font-bold">Founding Member Offer</h3>
              </div>
              <p className="text-white/90 text-sm md:text-base">
                Join the first 5,000 creators and lock in lifetime pricing.
                <span className="block text-white/80 text-xs md:text-sm mt-0.5">
                  Price never increases for founding members.
                </span>
              </p>
            </div>
            <div className="flex items-end justify-center md:justify-end gap-4 shrink-0">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold leading-none">$2.99</div>
                <div className="text-white/80 text-xs mt-1">/month</div>
              </div>
              <div className="h-10 w-px bg-white/30" />
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold leading-none">$24</div>
                <div className="text-white/80 text-xs mt-1">/year</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pricingPlans.map((plan) => {
            const Icon = plan.icon;
            const isYearly = billingCycle === 'yearly';
            // Enterprise is always per-seat/month; others switch monthly/annual.
            const price = plan.perSeat
              ? plan.price.monthly
              : (isYearly ? plan.price.yearly : plan.price.monthly);
            const priceSuffix = plan.perSeat
              ? '/user/month'
              : (isYearly ? '/year' : '/month');

            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                  plan.popular ? 'border-2 border-purple-500 shadow-xl scale-105' : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-center py-2 text-sm font-semibold">
                    <Crown className="w-4 h-4 inline mr-2" />
                    Most Popular
                  </div>
                )}
                
                <CardHeader className={`${plan.popular ? 'pt-12' : ''} ${plan.bgColor}`}>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>

                  {plan.founding && (
                    <div className="mt-3 inline-flex flex-col items-start gap-0.5 rounded-lg bg-orange-50 border border-orange-200 px-3 py-1.5">
                      <span className="text-xs font-bold text-orange-700">🔥 Founding Member Pricing</span>
                      <span className="text-[11px] text-orange-600">First 5,000 users only · Price locked forever</span>
                    </div>
                  )}

                  <div className="mt-4">
                    <span className="text-5xl font-bold text-gray-900">
                      {price === 0 ? 'Free' : `$${price}`}
                    </span>
                    {price > 0 && (
                      <span className="text-gray-500 ml-2">
                        {priceSuffix}
                        {!plan.perSeat && isYearly && (
                          <span className="block text-sm text-green-600 font-medium">
                            billed annually
                          </span>
                        )}
                        {plan.perSeat && (
                          <span className="block text-sm text-gray-500 font-medium">
                            minimum {plan.minSeats} seats
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <Button
                    className={`w-full h-12 font-semibold ${
                      plan.popular
                        ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white'
                        : ''
                    }`}
                    variant={plan.ctaVariant}
                    disabled={loadingPlan === plan.id}
                    onClick={() => handlePlanClick(plan.id)}
                  >
                    {loadingPlan === plan.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
                    ) : (
                      <>{plan.cta}<ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                  {checkoutError && loadingPlan === null && plan.popular && (
                    <p className="text-red-500 text-xs mt-2 text-center">{checkoutError}</p>
                  )}
                  
                  <div className="mt-8 space-y-4">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        {feature.included ? (
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <X className="w-3 h-3 text-gray-400" />
                          </div>
                        )}
                        <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                          {feature.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Compare Plans in Detail
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            See exactly what&apos;s included in each plan
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full max-w-5xl mx-auto">
            <thead>
              <tr className="border-b">
                <th className="text-left py-4 px-4 font-semibold text-gray-900">Features</th>
                <th className="text-center py-4 px-4 font-semibold text-gray-900">Free</th>
                <th className="text-center py-4 px-4 font-semibold text-purple-600 bg-purple-50 rounded-t-lg">Pro</th>
                <th className="text-center py-4 px-4 font-semibold text-gray-900">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: 'Designs', free: 'Unlimited', pro: 'Unlimited', enterprise: 'Unlimited' },
                { feature: 'Templates', free: 'Basic', pro: 'Premium', enterprise: 'Premium' },
                { feature: 'Storage', free: '2GB', pro: '50GB', enterprise: '500GB+ pooled' },
                { feature: 'Export Formats', free: 'PNG (max 1080px)', pro: 'PNG, PDF, SVG', enterprise: 'PNG, PDF, SVG' },
                { feature: 'AI Credits / month', free: '20', pro: '300', enterprise: '1,000 / seat' },
                { feature: 'Brand Kits', free: false, pro: '1', enterprise: 'Multiple' },
                { feature: 'Background Remover', free: false, pro: true, enterprise: true },
                { feature: 'Team Collaboration', free: false, pro: false, enterprise: true },
                { feature: 'Shared Workspaces', free: false, pro: false, enterprise: true },
                { feature: 'API Access', free: false, pro: false, enterprise: true },
                { feature: 'Support', free: 'Community', pro: 'Priority', enterprise: 'Priority' },
              ].map((row, index) => (
                <tr key={index} className="border-b">
                  <td className="py-4 px-4 text-gray-700">{row.feature}</td>
                  <td className="py-4 px-4 text-center">
                    {typeof row.free === 'boolean' ? (
                      row.free ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-gray-300 mx-auto" />
                    ) : (
                      <span className="text-gray-600">{row.free}</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center bg-purple-50">
                    {typeof row.pro === 'boolean' ? (
                      row.pro ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-gray-300 mx-auto" />
                    ) : (
                      <span className="text-purple-600 font-medium">{row.pro}</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {typeof row.enterprise === 'boolean' ? (
                      row.enterprise ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-gray-300 mx-auto" />
                    ) : (
                      <span className="text-gray-600">{row.enterprise}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600">Everything you need to know about our pricing</p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <Card key={index} className="overflow-hidden">
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900">{faq.question}</span>
                {openFaq === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {openFaq === index && (
                <div className="px-6 pb-4 text-gray-600">
                  {faq.answer}
                </div>
              )}
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-violet-600 to-fuchsia-600 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Ready to Start Creating?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/editor')}
              className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-8 h-14"
            >
              Start Free Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 h-14"
            >
              <Headphones className="w-5 h-5 mr-2" />
              Talk to Sales
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
